import React, { useState, useCallback } from 'react';
import {
  Box,
  Grid,
  Stack,
  Typography,
  Button,
  Divider,
  Alert,
  Snackbar,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { RestartAlt as ResetIcon, DeleteForever as DeleteIcon } from '@mui/icons-material';

import PageHeader from '../components/ui/PageHeader';
import Panel from '../components/ui/Panel';
import { useData } from '../context/DataContext';
import api from '../services/api';
import {
  CATEGORY_ORDER,
  DEFAULT_CATEGORY_COLORS,
  CATEGORY_COLORS,
  saveCustomColors,
  resetCategoryColors,
} from '../utils/colors';
import { TIME_FRAMES } from '../components/ui/FilterBar';

const PREFS_KEY = 'squid.preferences';

const loadPrefs = () => {
  try {
    return JSON.parse(localStorage.getItem(PREFS_KEY)) || {};
  } catch {
    return {};
  }
};

/**
 * Settings.
 *
 * Previously a static mockup — every control here now reads and writes real
 * state (localStorage preferences, category colours, destructive data reset).
 */
const Settings = () => {
  const { fetchAnalytics, fetchTickets } = useData();

  const [prefs, setPrefs] = useState(() => ({
    defaultTimeFrame: 'month',
    defaultRowsPerPage: 25,
    ...loadPrefs(),
  }));
  const [colors, setColors] = useState(() => ({ ...CATEGORY_COLORS }));
  const [toast, setToast] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  const updatePref = useCallback((key, value) => {
    setPrefs((prev) => {
      const next = { ...prev, [key]: value };
      localStorage.setItem(PREFS_KEY, JSON.stringify(next));
      return next;
    });
    setToast({ severity: 'success', message: 'Preference saved' });
  }, []);

  const handleColorChange = (category, value) => {
    setColors((prev) => ({ ...prev, [category]: value }));
  };

  const persistColors = () => {
    saveCustomColors(colors);
    setToast({ severity: 'success', message: 'Category colours saved — reload to apply everywhere' });
  };

  const handleResetColors = () => {
    resetCategoryColors();
    setColors({ ...DEFAULT_CATEGORY_COLORS });
    setToast({ severity: 'info', message: 'Category colours reset to defaults' });
  };

  const handleClearData = async () => {
    setClearing(true);
    try {
      // The endpoint returns 202 + jobId; the delete runs in the background so
      // it cannot be cut off by the 30s CloudFront origin timeout (which
      // previously surfaced as "status code 504").
      const started = await api.delete('/tickets');
      const jobId = started.data?.jobId;

      let deletedCount = null;
      if (jobId) {
        const startedAt = Date.now();
        // eslint-disable-next-line no-constant-condition
        while (true) {
          if (Date.now() - startedAt > 15 * 60 * 1000) {
            throw new Error('Still running — check back shortly.');
          }
          // eslint-disable-next-line no-await-in-loop
          const { data } = await api.get(`/jobs/${jobId}`);
          if (data.job.status === 'succeeded') {
            deletedCount = data.job.result?.deletedCount ?? null;
            break;
          }
          if (data.job.status === 'failed') throw new Error(data.job.error || 'Job failed');
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      }

      await Promise.all([fetchTickets(), fetchAnalytics()]);
      setToast({
        severity: 'success',
        message:
          deletedCount === null
            ? 'All ticket data cleared'
            : `Deleted ${deletedCount} tickets`,
      });
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Failed to clear data';
      setToast({ severity: 'error', message });
    } finally {
      setClearing(false);
      setConfirmOpen(false);
    }
  };

  return (
    <Box>
      <PageHeader title="Settings" subtitle="Preferences are stored in this browser." />

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel title="Defaults" subtitle="Applied when you open the app">
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Default time frame"
                value={prefs.defaultTimeFrame}
                onChange={(e) => updatePref('defaultTimeFrame', e.target.value)}
              >
                {TIME_FRAMES.filter((t) => t.value !== 'custom').map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                fullWidth
                size="small"
                label="Rows per page"
                value={prefs.defaultRowsPerPage}
                onChange={(e) => updatePref('defaultRowsPerPage', Number(e.target.value))}
              >
                {[25, 50, 100, 250].map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Panel>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Panel
            title="Category colours"
            subtitle="Used across every chart and badge"
            action={
              <Button size="small" color="inherit" startIcon={<ResetIcon />} onClick={handleResetColors}>
                Reset
              </Button>
            }
          >
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {CATEGORY_ORDER.map((category) => (
                <Stack key={category} direction="row" alignItems="center" spacing={2}>
                  <Box
                    component="input"
                    type="color"
                    aria-label={`${category} colour`}
                    value={colors[category] || DEFAULT_CATEGORY_COLORS[category]}
                    onChange={(e) => handleColorChange(category, e.target.value)}
                    sx={{
                      width: 40,
                      height: 28,
                      p: 0,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'transparent',
                      cursor: 'pointer',
                    }}
                  />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {category}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {(colors[category] || '').toUpperCase()}
                  </Typography>
                </Stack>
              ))}
              <Divider />
              <Button variant="contained" size="small" onClick={persistColors} sx={{ alignSelf: 'flex-start' }}>
                Save colours
              </Button>
            </Stack>
          </Panel>
        </Grid>

        <Grid size={12}>
          <Panel title="Danger zone">
            <Alert severity="warning" sx={{ mb: 2 }}>
              Clearing removes every ticket from the database. Data must be re-imported or re-synced from Jira.
            </Alert>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setConfirmOpen(true)}
            >
              Clear all ticket data
            </Button>
          </Panel>
        </Grid>
      </Grid>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Clear all ticket data?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently deletes every ticket stored in the database. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleClearData} disabled={clearing}>
            {clearing ? 'Clearing…' : 'Delete everything'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default Settings;
